import { useCallback } from 'react';
import { isEmpty } from 'lodash';
import { Typography, Stack } from '@mui/material';
import { makeStyles } from 'tss-react/mui';
import { useDispatch, useListeners, useSelector } from '@client/hooks/redux.hooks';
import { TextArea } from '@client/components/forms/TextArea';
import { getSelectedProfile } from '@client/selectors/helpers/profile';
import { ProfileTypes } from '@sda/enums';
import { Tip } from '@client/components/common/Tip';
import { baseUrl } from '@sda/utils/url';
import { SignupWrapperForm } from '../../common/SignupWrapper/SignupWrapperForm';
import { signupArtistInfoListeneners, startSaveArtistInfo } from './signup-artist-info.listeners';
import { SignupArtistInfoRequiredSchema, SignupArtistInfoSchema } from './signup-artist-info.schema';
import type { SignupStepProps } from '../../common/interfaces';

const useStyles = makeStyles()((theme) => ({
  tipContainer: {
    textAlign: 'left',

    '& > p': {
      marginBottom: theme.spacing(1),

      [theme.breakpoints.up('md')]: {
        marginBottom: theme.spacing(2),
      },
    },
  },
}));

const Explainer = () => {
  const { classes } = useStyles();

  return (
    <Stack spacing={2}>
      <Typography
        variant="h6"
        align="left"
      >
        Write a short bio
      </Typography>

      <div className={classes.tipContainer}>
        <Typography
          color="textSecondary"
          align="left"
        >
          Let hosts know what makes your project special, and why they would want you to perform in their space.
        </Typography>

        <Tip
          id="sda-artist-bio-link"
          label="Tip:"
          tipText="What to include in your bio"
          linkTo={baseUrl('/support/en/articles/8770841-artists-what-to-include-in-your-bio')}
        />
      </div>
    </Stack>
  );
};

export const Info: React.FC<SignupStepProps> = ({ skippable = true }) => {
  const dispatch = useDispatch();
  const selectedProfile = useSelector(getSelectedProfile);

  useListeners(signupArtistInfoListeneners);

  const initialValues = {
    bioHTML: selectedProfile?.profileType === ProfileTypes.ACT ? selectedProfile.bioHTML || '' : '',
  };

  const handleSubmit = useCallback((values: typeof initialValues) => {
    dispatch(startSaveArtistInfo(values));
  }, [dispatch]);

  return (
    <SignupWrapperForm
      steps={6}
      currentStep={4}
      renderExplainer={<Explainer />}
      FormikProps={{
        initialValues,
        onSubmit: handleSubmit,
        validationSchema: skippable ? SignupArtistInfoSchema : SignupArtistInfoRequiredSchema,
      }}
      buttonProps={({ values }) => ({
        scope: 'signup-act-info-form',
        ButtonProps: {
          disabled: !skippable && isEmpty(values.bioHTML),
        },
      })}
    >
      <TextArea
        name="bioHTML"
        label="Bio"
        maxLength={1000}
        minRows={6}
        required={!skippable}
        fullWidth
      />
    </SignupWrapperForm>
  );
};
